/**
 * What the Workshop page offers every part inside it: where to go (a new chat,
 * Projects, a project, a chat), the dialogs it owns (New project, Search) and
 * the one chat whose sidebar row is being renamed.
 */
import { createContext, useContext } from "react";
import type { WorkshopQuickCreateItem } from "@/types/workshop.type";

/** Which page the Workshop's main column shows. */
export type WorkshopSurface = "home" | "projects" | "creos" | "project" | "thread";

export type WorkshopDeskApi = {
  brandId: string;
  /** The brand's name, for "What are we making for …?" and the Projects line. */
  brandName: string | null;
  activeProjectId: string | null;
  activeThreadId: string | null;
  /** The project the new chat will live in: its folder opens in the sidebar. */
  pickedProjectId: string | null;
  setPickedProjectId: (projectId: string | null) => void;
  /** A new chat (the home). With a project, the picker starts on it. */
  newChat: (projectId?: string | null) => void;
  openProjects: () => void;
  /** The Creos page: every skill and agent as a card. */
  openCreos: () => void;
  openProject: (projectId: string) => void;
  openThread: (projectId: string, threadId: string) => void;
  /** New project; `onCreated` hears the new id (the picker, Move to). */
  openNewProject: (onCreated?: (projectId: string) => void) => void;
  openSearch: () => void;
  /** Use it: a new chat in that project (null: none yet) with the item's pill in its composer. */
  startQuickCreate: (item: WorkshopQuickCreateItem, projectId: string | null) => void;
  /** Rename turns the chat's sidebar row into a field. */
  renamingThreadId: string | null;
  startRename: (threadId: string) => void;
  endRename: () => void;
};

export const WorkshopDeskContext = createContext<WorkshopDeskApi | null>(null);

export function useWorkshopDesk(): WorkshopDeskApi {
  const desk = useContext(WorkshopDeskContext);
  if (!desk) throw new Error("useWorkshopDesk outside the Workshop page");
  return desk;
}

/** The same, or null where a part is also used outside the page. */
export function useOptionalWorkshopDesk(): WorkshopDeskApi | null {
  return useContext(WorkshopDeskContext);
}
